import { useEffect } from "react";
import { App, Form, Input, Modal, Segmented } from "antd";
import { useTranslation } from "react-i18next";
import type { FactoryDto } from "@audit-plan/shared";
import { ApiError } from "../../api/client";
import { useSaveFactory, type FactoryInput } from "../../api/factories";
import { useModalProps } from "../useModalProps";

/** Nuovo stabilimento di un fornitore (supplierId) o modifica (factory). */
export function FactoryFormModal(props: { open: boolean; onClose: () => void; supplierId?: string; factory?: FactoryDto | null; onSaved?: (f: FactoryDto) => void }) {
  const { open, onClose, supplierId, factory, onSaved } = props;
  const { t } = useTranslation();
  const { message } = App.useApp();
  const modalProps = useModalProps(640);
  const [form] = Form.useForm();
  const save = useSaveFactory();

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(factory ? { ...factory, code: factory.code ?? "" } : { tier: 2 });
  }, [open, factory, form]);

  const submit = async (v: Record<string, string | number>) => {
    const body: FactoryInput = {
      code: String(v.code ?? "").trim(),
      name: String(v.name ?? "").trim(),
      nameLocal: String(v.nameLocal ?? ""),
      // sensitive-ok: invio del form
      address: String(v.address ?? ""),
      city: String(v.city ?? ""),
      country: String(v.country ?? ""),
      tier: Number(v.tier) === 3 ? 3 : 2,
    };
    try {
      const f = await save.mutateAsync({ id: factory?.id, supplierId, body });
      void message.success(t("saved"));
      onSaved?.(f);
      onClose();
    } catch (e) {
      void message.error(e instanceof ApiError ? e.message : t("required"));
    }
  };

  return (
    <Modal open={open} onCancel={onClose} onOk={() => form.submit()} okText={t("save")} cancelText={t("cancel")} confirmLoading={save.isPending} {...modalProps} title={factory ? `${t("edit")} · ${factory.name}` : t("f_new")} destroyOnHidden>
      <Form form={form} layout="vertical" onFinish={submit} requiredMark="optional">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))", columnGap: 16 }}>
          <Form.Item name="name" label={t("f_factory")} rules={[{ required: true, message: t("required") }]} style={{ gridColumn: "1 / -1" }}>
            <Input />
          </Form.Item>
          <Form.Item name="code" label={t("sapCode")} extra={t("f_codeHelp")}>
            <Input />
          </Form.Item>
          <Form.Item name="tier" label={t("f_tier")}>
            <Segmented block options={[{ value: 2, label: t("f_tier2") }, { value: 3, label: t("f_tier3") }]} />
          </Form.Item>
          <Form.Item name="nameLocal" label={t("f_nameLocal")} style={{ gridColumn: "1 / -1" }}>
            <Input />
          </Form.Item>
          <Form.Item name="address" label={t("f_address")} style={{ gridColumn: "1 / -1" }}>
            <Input data-sensitive />
          </Form.Item>
          <Form.Item name="city" label={t("f_city")}>
            <Input />
          </Form.Item>
          <Form.Item name="country" label={t("country")}>
            <Input />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
}
